import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import { AuthProvider } from './components/FirebaseAuth';
import { CssBaseline, Box, Paper, Stack, Typography, Button } from '@mui/material';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import BrowserNotSupportedRoundedIcon from '@mui/icons-material/BrowserNotSupportedRounded';
import PublicRouter from './routers/PublicRouter';
import AuthRouter from './routers/AuthRouter';
import brand from "./inc/branding.json";

import PublicTemplate from './components/templates/PublicTemplate';
import AppTemplate from './components/templates/AppTemplate';

import SignIn from './pages/public/SignIn';
import SignUp from './pages/public/SignUp';
import ForgotPassword from './pages/public/ForgotPassword';
import ToS from './pages/public/ToS';
import Privacy from './pages/public/Privacy';
import PageNotFound from './pages/public/PageNotFound';

import UserProfile from './pages/auth/user/UserProfile';
import UserSecurity from './pages/auth/user/UserSecurity';
import ViewLogs from './pages/auth/user/ViewLogs';
import MFAEnroll from './pages/auth/user/MFAEnroll';
import MFAVerify from './pages/auth/user/MFAVerify';

import Home from './pages/auth/dashboard/Home';
import ContractorDashboard from './pages/auth/contractor/Dashboard';
import OnboardingContractor from './pages/auth/contractor/Onboarding';
import OnboardingClient from './pages/auth/client/Onboarding';
import ClientDashboard from './pages/auth/client/Dashboard';
import AdminDashboard from "./pages/auth/admin/Dashboard";
import DashboardSearch from "./pages/auth/search/DashboardSearch";
import { isBrowserSupported } from './config/browserSupport';

function App() {
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const [lastCheckedAt, setLastCheckedAt] = useState(new Date());
	const wasOfflineRef = useRef(!navigator.onLine);
	const isSupportedBrowser = isBrowserSupported();
	const dba = brand.brandConfig?.config?.dba;

	useEffect(() => {
		const handleStatusChange = () => {
			const online = navigator.onLine;
			setIsOnline(online);
			setLastCheckedAt(new Date());

			if (!online) {
				wasOfflineRef.current = true;
				return;
			}

			// Force a clean app state after reconnect.
			if (wasOfflineRef.current) {
				window.location.reload();
			}
		};
	
		window.addEventListener('online', handleStatusChange);
		window.addEventListener('offline', handleStatusChange);
	
		return () => {
		  window.removeEventListener('online', handleStatusChange);
		  window.removeEventListener('offline', handleStatusChange);
		};
	}, []);

	return (
		<>
			{!isSupportedBrowser ? (
				<Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'background.default' }}>
					<CssBaseline />
					<Paper variant="outlined" sx={{ width: '100%', maxWidth: 640, borderRadius: 2, p: { xs: 2.5, sm: 3 } }}>
						<Stack spacing={2} alignItems="center" textAlign="center">
							<Box
								sx={(theme) => ({
									width: 56,
									height: 56,
									borderRadius: 1.5,
									display: 'grid',
									placeItems: 'center',
									bgcolor: theme.palette.grey[200],
									color: theme.palette.text.secondary,
								})}
							>
								<BrowserNotSupportedRoundedIcon />
							</Box>
							<Typography variant="h6" sx={{ fontWeight: 700 }}>
								Unsupported Browser
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
								{dba} currently supports Google Chrome and Mozilla Firefox for the best security and feature compatibility. Please open this app in one of the supported browsers.
							</Typography>
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
								<Button
									variant="outlined"
									size="small"
									component="a"
									href="https://www.google.com/chrome/"
									target="_blank"
									rel="noopener noreferrer"
								>
									Download Chrome
								</Button>
								<Button
									variant="outlined"
									size="small"
									component="a"
									href="https://www.mozilla.org/firefox/new/"
									target="_blank"
									rel="noopener noreferrer"
								>
									Download Firefox
								</Button>
							</Stack>
						</Stack>
					</Paper>
				</Box>
			) : isOnline ? (
				<AuthProvider>
					<Router>
						<Switch>
							<AuthRouter exact path="/" component={Home} template={AppTemplate} title="Home" />
							<AuthRouter exact path="/client/dashboard" component={ClientDashboard} template={AppTemplate} title="Client Dashboard" allowRoles={["client"]} />
							<AuthRouter exact path="/client/dashboard/search" component={DashboardSearch} template={AppTemplate} title="Search Contractors" allowRoles={["client"]} />
							<AuthRouter exact path="/admin/dashboard" component={AdminDashboard} template={AppTemplate} title="Admin Dashboard" allowRoles={["admin"]} />
							<AuthRouter exact path="/admin/dashboard/search" component={DashboardSearch} template={AppTemplate} title="Search Contractors" allowRoles={["admin"]} />
							<AuthRouter exact path="/contractor/dashboard" component={ContractorDashboard} template={AppTemplate} title="Contractor Dashboard" allowRoles={["contractor"]} />
							<AuthRouter exact path="/contractor/onboarding" component={OnboardingContractor} template={AppTemplate} title="Contractor Setup" allowRoles={["contractor"]} />
							<AuthRouter exact path="/client/onboarding" component={OnboardingClient} template={AppTemplate} title="Client Setup" allowRoles={["client"]} />
							<AuthRouter exact path="/user/profile" component={UserProfile} template={AppTemplate} title="Profile" />
							<AuthRouter exact path="/user/security" component={UserSecurity} template={AppTemplate} title="Security" />
							<AuthRouter exact path="/user/log" component={ViewLogs} template={AppTemplate} title="Audit Logs" />
							<AuthRouter exact path="/mfa/enroll" component={MFAEnroll} template={AppTemplate} title="Manage MFA" />
							<AuthRouter exact path="/mfa/verify" component={MFAVerify} template={AppTemplate} title="Verify MFA" />
							<PublicRouter exact path="/signin" component={SignIn} template={PublicTemplate} title="Sign In" />
							<PublicRouter exact path="/signup" component={SignUp} template={PublicTemplate} title="Sign Up" />
							<PublicRouter exact path="/forgot-password" component={ForgotPassword} template={PublicTemplate} title="Forgot Password" />
							<PublicRouter exact path="/terms" component={ToS} template={PublicTemplate} title="Terms of Service" />
							<PublicRouter exact path="/privacy" component={Privacy} template={PublicTemplate} title="Privacy Policy" />
							<PublicRouter component={PageNotFound} template={PublicTemplate} title="Not Found" />
						</Switch>
					</Router>
				</AuthProvider>	
			) : (
				<Box
					sx={{
						minHeight: '100vh',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						p: 2,
						background:
							'radial-gradient(circle at 18% 12%, rgba(25,118,210,0.10), transparent 28%), radial-gradient(circle at 82% 18%, rgba(5,105,13,0.08), transparent 26%), linear-gradient(180deg, #f8fafd 0%, #eef3f7 100%)',
					}}
				>
					<CssBaseline />
					<Paper
						variant="outlined"
						sx={{
							width: '100%',
							maxWidth: 560,
							borderRadius: 3,
							p: { xs: 3, sm: 4 },
							position: 'relative',
							overflow: 'hidden',
							boxShadow: '0 18px 54px rgba(60,64,67,0.10)',
							'&:before': {
								content: '""',
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								height: 5,
								background: 'linear-gradient(90deg, #05690D, #1976D2)',
							},
						}}
					>
						<Stack spacing={2.25} alignItems="center" textAlign="center">
							<Box
								sx={(theme) => ({
									width: 68,
									height: 68,
									borderRadius: 2.5,
									display: 'grid',
									placeItems: 'center',
									bgcolor: 'rgba(25, 118, 210, 0.10)',
									color: theme.palette.primary.main,
									border: '1px solid rgba(25, 118, 210, 0.18)',
								})}
							>
								<WifiOffRoundedIcon sx={{ fontSize: 36 }} />
							</Box>
							<Stack spacing={0.75} alignItems="center">
								<Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 850, letterSpacing: 1 }}>
									Connection paused
								</Typography>
								<Typography variant="h4" sx={{ fontWeight: 850, color: '#30343b', letterSpacing: '-0.03em', fontSize: { xs: '1.8rem', sm: '2.1rem' } }}>
									You are offline
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 430, lineHeight: 1.65 }}>
									{dba} cannot reach the network right now. We will refresh automatically when your connection comes back.
								</Typography>
							</Stack>
							<Button
								variant="contained"
								startIcon={<RefreshRoundedIcon />}
								onClick={() => {
									setLastCheckedAt(new Date());
									if (navigator.onLine) {
										window.location.reload();
									}
								}}
								sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2, px: 2.5 }}
							>
								Retry connection
							</Button>
							<Box sx={{ px: 1.5, py: 0.75, borderRadius: 2, bgcolor: 'rgba(60,64,67,0.06)' }}>
								<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
									Last checked: {lastCheckedAt.toLocaleTimeString()}
								</Typography>
							</Box>
						</Stack>
					</Paper>
				</Box>
			)}
		</>
	);
}

export default App;
